import { currentYear, navigateToTop } from "../../utils/Constants";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { requestPasswordGate } from "./PasswordGateway";
import useApiProfile from "../../hooks/useApiProfile";
import "../../styles/layout/_footer.scss";

const LAST_ACTIVITY_KEY = "moozLastActivityAt";
const TIMEOUT_RETURN_TO_KEY = "moozTimeoutReturnTo";

const NAV_GROUPS = [
    {
        label: "Product",
        links: [
            { label: "Demo", to: "/#demo", demo: true },
            // { label: "Pricing", to: "/pricing" },
            { label: "FAQs", to: "/faqs" },
        ],
    },
    {
        label: "Company",
        links: [
            { label: "About", to: "/about" },
            { label: "Dashboard", to: "/dashboard" },
            { label: "Contact", to: "/contact" },
        ],
    },
    {
        label: "Legal",
        links: [
            { label: "Privacy", to: "/help/privacy-policy" },
            { label: "Cookies", to: "/help/cookies" },
            { label: "Terms", to: "/help/terms-of-service" },
        ],
    },
];

export default function Footer() {
    const { isAuthenticated, logout } = useAuth0();
    const { profile } = useApiProfile();
    const location = useLocation();
    const navigate = useNavigate();
    const canViewAdmin =
        profile?.can_view_admin ??
        (profile?.admin_role ? profile.admin_role !== "user" : false);

    const handleDemoClick = (event) => {
        event.preventDefault();
        const el = document.getElementById("demo");
        if (location.pathname === "/" && el) {
            window.location.hash = "demo";
            el.scrollIntoView({ behavior: "auto", block: "start" });
            return;
        }
        navigate({ pathname: "/", hash: "#demo" });
    };

    const handleLogout = () => {
        localStorage.removeItem(LAST_ACTIVITY_KEY);
        localStorage.removeItem(TIMEOUT_RETURN_TO_KEY);
        logout({ logoutParams: { returnTo: window.location.origin } });
    };
    const handleLogin = (e) => {
        e.preventDefault();
        requestPasswordGate({ type: "auth0Login" });
    };

    return (
        <footer className="footer" id="footer" aria-label="Site footer">
            <div className="footer__inner">
                {/* ── Top row: brand + nav groups ── */}
                <div className="footer__top">
                    {/* Brand */}
                    <div className="footer__brand-col">
                        <Link
                            to="/"
                            onClick={navigateToTop}
                            className="footer__logo"
                        >
                            <span
                                className="footer__logo-icon"
                                aria-hidden="true"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="22"
                                    height="22"
                                    viewBox="0 0 24 24"
                                    fill="currentColor"
                                    stroke="none"
                                >
                                    <path d="M20.84 12c0 2.05 .985 3.225 -.04 5c-1.026 1.775 -2.537 1.51 -4.314 2.534c-1.776 1.026 -2.302 2.466 -4.353 2.466c-2.051 0 -2.576 -1.441 -4.353 -2.466c-1.776 -1.024 -3.288 -.759 -4.314 -2.534c-1.025 -1.775 -.04 -2.95 -.04 -5s-.985 -3.225 .04 -5c1.026 -1.775 2.537 -1.51 4.314 -2.534c1.776 -1.026 2.302 -2.466 4.353 -2.466s2.577 1.441 4.353 2.466c1.776 1.024 3.288 .759 4.313 2.534c1.026 1.775 .04 2.95 .04 5z" />
                                </svg>
                            </span>
                            <span className="footer__logo-name">Mooz</span>
                        </Link>

                        <p className="footer__tagline">
                            Master the art of interviews. Practice, refine, and
                            land the role.
                        </p>

                        {/* Auth CTA */}
                        <div className="footer__auth">
                            {isAuthenticated ? (
                                <button
                                    className="footer__auth-btn"
                                    onClick={handleLogout}
                                >
                                    Log out
                                </button>
                            ) : (
                                <Link
                                    to="/"
                                    className="footer__auth-btn"
                                    onClick={handleLogin}
                                >
                                    Log in
                                </Link>
                            )}
                        </div>
                    </div>

                    {/* Nav groups */}
                    <nav className="footer__nav" aria-label="Footer navigation">
                        {NAV_GROUPS.map((group) => (
                            <div
                                className="footer__nav-group"
                                key={group.label}
                            >
                                <h3 className="footer__nav-heading">
                                    {group.label}
                                </h3>
                                <ul className="footer__nav-list">
                                    {group.links.map((link) => {
                                        // Hide Demo for authenticated users
                                        if (link.demo && isAuthenticated)
                                            return null;

                                        if (link.href) {
                                            return (
                                                <li key={link.label}>
                                                    <a
                                                        href={link.href}
                                                        className="footer__nav-link"
                                                    >
                                                        {link.label}
                                                    </a>
                                                </li>
                                            );
                                        }

                                        return (
                                            <li key={link.label}>
                                                <Link
                                                    to={link.to}
                                                    className="footer__nav-link"
                                                    onClick={
                                                        link.demo
                                                            ? handleDemoClick
                                                            : navigateToTop
                                                    }
                                                >
                                                    {link.label}
                                                </Link>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        ))}
                    </nav>
                </div>

                {/* ── Bottom bar ── */}
                <div className="footer__bottom" role="contentinfo">
                    <span className="footer__copyright">
                        &copy; {currentYear} Mooz Video Interview. All rights
                        reserved.
                    </span>
                    <div>
                        <Link
                            to="/help"
                            className="footer__help-link"
                            onClick={navigateToTop}
                        >
                            Help centre
                        </Link>
                        {/* <button
                            type="button"
                            className="footer__help-link"
                            onClick={() => {
                                try {
                                    window.dispatchEvent(
                                        new Event("mooz:openCookieSettings")
                                    );
                                } catch {
                                    // noop
                                }
                            }}
                        >
                            Cookie settings
                        </button> */}
                        {canViewAdmin && (
                            <Link
                                to="/administration"
                                className="footer__help-link"
                                onClick={navigateToTop}
                            >
                                Administration
                            </Link>
                        )}
                    </div>
                </div>
            </div>
        </footer>
    );
}
